import React, { useEffect } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { setBreadcrumbItems } from "../../store/actions"

/**
 * Página placeholder reutilizable para las pantallas de Envío de correos
 * que aún no se construyen. Muestra título, descripción del scope planeado
 * y los endpoints que va a consumir cuando se implemente.
 */
const Stub = ({
  empresaSeleccionada, user, setBreadcrumbItems: setBreadcrumb,
  title, descripcion, alcance, endpoints, icon,
}) => {
  const nitemisor = empresaSeleccionada?.nit || empresaSeleccionada?.identificacion || ""
  const empresaNombre = empresaSeleccionada?.nombre || ""

  useEffect(() => {
    document.title = `${title} | LS-DA`
    const greeting = user
      ? `Bienvenido, ${user.nombre || ""} ${user.apellido || ""}`.trim()
      : title
    setBreadcrumb(greeting, [
      { title: "LS-DA", link: "#" },
      { title: "Envío de correos", link: "#" },
      { title, link: "#" },
    ])
  }, [user, setBreadcrumb, title])

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ background: "#fff", borderRadius: 14, padding: "20px 24px", border: "1px solid #e2e8f0" }}>
        <div>
          <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4 }}>
            Envío de correos
          </div>
          <h3 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#0f172a" }}>{title}</h3>
          {nitemisor && (
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
              <i className="mdi mdi-domain me-1 text-primary" />
              <strong>{empresaNombre}</strong> · NIT {nitemisor}
            </div>
          )}
        </div>
      </div>

      <div style={{
        background: "linear-gradient(135deg, #eff6ff, #ffffff)",
        border: "1px solid #bfdbfe", borderLeft: "4px solid #3b82f6",
        borderRadius: 14, padding: "32px 40px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14,
            background: "#3b82f61a", color: "#3b82f6",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 28, flexShrink: 0,
          }}>
            <i className={`mdi ${icon || "mdi-tools"}`} />
          </div>
          <div>
            <span style={{
              padding: "3px 10px", borderRadius: 999,
              background: "#fbbf24", color: "#78350f",
              fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4,
            }}>
              <i className="mdi mdi-wrench me-1" />Próximamente
            </span>
            <h4 style={{ margin: "8px 0 0", fontSize: 18, color: "#0f172a", fontWeight: 700 }}>
              {descripcion}
            </h4>
          </div>
        </div>

        {alcance && alcance.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 11, color: "#1e40af", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.4, marginBottom: 8 }}>
              <i className="mdi mdi-check-decagram me-1" />
              Funcionalidad planeada
            </div>
            <ul style={{ margin: 0, paddingLeft: 22, color: "#475569", fontSize: 14, lineHeight: 1.8 }}>
              {alcance.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </div>
        )}

        {endpoints && endpoints.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 11, color: "#1e40af", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.4, marginBottom: 8 }}>
              <i className="mdi mdi-api me-1" />
              Endpoints que va a consumir
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {endpoints.map((ep, i) => (
                <div key={i} style={{
                  background: "#fff", border: "1px solid #e2e8f0",
                  borderRadius: 8, padding: "8px 12px",
                  fontFamily: "monospace", fontSize: 12, color: "#0f172a",
                }}>
                  <span style={{
                    padding: "1px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700,
                    background: ep.method === "GET" ? "#dbeafe" : ep.method === "POST" ? "#dcfce7" : "#fef3c7",
                    color: ep.method === "GET" ? "#1e40af" : ep.method === "POST" ? "#166534" : "#854d0e",
                    marginRight: 8,
                  }}>{ep.method}</span>
                  {ep.path}
                  {ep.desc && (
                    <span style={{ color: "#64748b", fontFamily: "inherit", marginLeft: 8, fontSize: 11 }}>
                      — {ep.desc}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

Stub.propTypes = {
  empresaSeleccionada: PropTypes.object,
  user: PropTypes.object,
  setBreadcrumbItems: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  descripcion: PropTypes.string.isRequired,
  alcance: PropTypes.arrayOf(PropTypes.string),
  endpoints: PropTypes.arrayOf(PropTypes.shape({
    method: PropTypes.string,
    path: PropTypes.string,
    desc: PropTypes.string,
  })),
  icon: PropTypes.string,
}

const mapStateToProps = state => ({
  empresaSeleccionada: state.Login.empresaSeleccionada,
  user: state.Login.user,
})

export default connect(mapStateToProps, { setBreadcrumbItems })(Stub)
